import { Children, useEffect, useState } from "react";
import PropTypes from "prop-types";
import WebIcons from "@/components/common/Web_Icons";
import { Checkbox } from "antd";

const EMPTY_SELECTED_ITEMS = Object.freeze({});

const areSelectedItemsEqual = (a, b) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return bKeys.every((key) => a[key] === b[key]);
};

const CustomCollapse = ({
  title,
  subtitle = "",
  children,
  isShadow,
  isMarginBottom,
  onChildSelect = undefined,
  showCheckbox = false,
  defaultOpen = false,
  isOpen = undefined,
  onToggle = undefined,
  initialSelectedItems,
  className = "",
}) => {
  const resolvedInitialItems = initialSelectedItems ?? EMPTY_SELECTED_ITEMS;

  // Use controlled or uncontrolled state based on props
  const controlled = isOpen !== undefined && onToggle !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [selectedItems, setSelectedItems] = useState(resolvedInitialItems);

  useEffect(() => {
    if (!showCheckbox) return;

    setSelectedItems((prev) => {
      if (areSelectedItemsEqual(prev, resolvedInitialItems)) return prev;
      return resolvedInitialItems;
    });
  }, [resolvedInitialItems, showCheckbox]);

  const childArray = Children.toArray(children);

  const isCollapsedOpen = controlled ? isOpen : internalOpen;

  const toggleCollapse = () => {
    if (controlled) {
      onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  const handleCheckboxChange = (index, checked) => {
    const newSelectedItems = {
      ...selectedItems,
      [index]: checked,
    };
    setSelectedItems(newSelectedItems);

    if (onChildSelect) {
      const childContent = childArray[index]?.props?.children?.find?.(
        (child) => child.type === "h1"
      )?.props.children;

      onChildSelect({
        ...newSelectedItems,
        itemText: {
          ...selectedItems,
          [index]: childContent,
        },
      });
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Header Section */}
      <div
        className={`bg-[var(--header-bg)] rounded-md ${
          isShadow ? "shadow-md" : ""
        } transition-all duration-300 ${
          isCollapsedOpen ? "rounded-b-none" : ""
        }`}>
        <div
          onClick={toggleCollapse}
          className="flex items-center justify-between px-2.5 py-1.5 cursor-pointer">
          <div>
            <h1 className="font-bold text-bold">{title}</h1>
            <p className="dark-mode-text text-small">{subtitle}</p>
          </div>
          <span
            className={`transform transition-transform duration-300 ${
              isCollapsedOpen ? "rotate-180" : ""
            }`}>
            <WebIcons icon="ArrowDown" size="small" />
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div
        className={`transition-max-height duration-500 ease-in-out mt-3 ${
          isCollapsedOpen
            ? "max-h-[400px] overflow-y-auto"
            : "max-h-0 overflow-hidden"
        }`}>
        {isCollapsedOpen &&
          childArray.map((child, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-4 bg-[var(--header-bg)] border rounded-md shadow-sm ${
                isMarginBottom && "mb-4"
              }`}>
              <div className={`${showCheckbox ? "flex-grow" : "w-full"}`}>
                {child}
              </div>
              {showCheckbox && (
                <div className="ml-4">
                  <Checkbox
                    checked={selectedItems[index]}
                    onChange={(e) =>
                      handleCheckboxChange(index, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

CustomCollapse.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.node),
  isShadow: PropTypes.bool,
  isMarginBottom: PropTypes.bool,
  onChildSelect: PropTypes.func,
  showCheckbox: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  initialSelectedItems: PropTypes.object,
  className: PropTypes.string,
};

export default CustomCollapse;
